import {
  faComment,
  faExclamationCircle,
  faLock,
  faTaxi,
  faToolbox,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AiSparkle from 'components/icons/AiSparkle';
import {
  AssignmentStatusEnum,
  AssignmentViewModel,
  AssignmentWithAutoApprovalCondition,
  CompanyModel,
  InvoiceExtraStatus,
} from 'api/model';
import ApprovalChecksWidget from 'components/AssignmentApproval/ApprovalChecksWidget';
import ExpandableWarningsWidget from 'components/AssignmentsList/ExpandableWarningsWidget';
import FakeCheckbox from 'components/inputs/FakeCheckbox';
import {
  ColumnSetting,
  tableCellClassName,
  TableColumnEnum,
  tableHeadClassName,
} from 'components/Table/utils';
import useCompanies from 'contexts/basicData/useCompanies';
import useUsers from 'contexts/basicData/useUsers';
import useAssignmentWarnings, {
  WarningInfo,
} from 'hooks/useAssignmentWarnings';
import useHasAccessToInvoiceExtras from 'hooks/useHasAccessToInvoiceExtras';
import {
  CheckboxCell,
  ColumnRenderProps,
  specialBulkSort,
} from 'pages/GlobalSearch/BulkApproveAssignmentsList/useColumnSettings';
import { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { makeBooleanComparator, makeNumberComparator } from 'utils/sorting';

import {
  formatDate,
  formatDateTime,
  formatTime,
  getHoursAndMinutesFromMillisecondsString,
} from 'utils/date-helpers';
import {
  getManualApprovalUnlockTime,
  isApprovableStatus,
  isManualApprovalUnlocked,
} from './constants';

const CustomIcon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.colors.background.button};
`;

const StatusWithIcon = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
`;

const formatKr = (value: number): string =>
  Number.isInteger(value) ? `${value} kr` : `${value.toFixed(2)} kr`;

export const STATUSES_TO_BE_APPROVED = [
  AssignmentStatusEnum.Complete,
  AssignmentStatusEnum.Canceled,
  AssignmentStatusEnum.Aborted,
] as AssignmentStatusEnum[];

const useAutoApprovalTableColumnSettings = () => {
  const assignmentWarnings = useAssignmentWarnings();
  const users = useUsers();
  const { companies: companiesLookup } = useCompanies();
  const hasAccessToInvoiceExtras = useHasAccessToInvoiceExtras();

  const companiesDict = useMemo(() => {
    return companiesLookup
      .filter((c) => c.active)
      .reduce(
        (acc, curr) => {
          let companyAddress = `${curr.address}${curr.zip}${curr.city}`;
          companyAddress = companyAddress.toLowerCase().replaceAll(' ', '');
          acc[companyAddress] = curr;
          return acc;
        },
        {} as { [lookupString: string]: CompanyModel }
      );
  }, [companiesLookup]);
  const logisticsStrangnas = useMemo(
    () => companiesLookup.find((c) => c.isLogisticsCenter),
    [companiesLookup]
  );

  const getVisibleFromAddressName = useCallback(
    (avm: AssignmentWithAutoApprovalCondition) => {
      const { fromAddress, fromZip, fromCity } = avm.assignment.assignment;
      const address = `${fromAddress}${fromZip}${fromCity}`
        .toLowerCase()
        .replaceAll(' ', '');
      return (
        companiesDict[address]?.name ??
        `${fromAddress}, ${fromZip} ${fromCity}`
      );
    },
    [companiesDict]
  );

  const getVisibleToAddressName = useCallback(
    (avm: AssignmentWithAutoApprovalCondition) => {
      const { toAddress, toZip, toCity } = avm.assignment.assignment;
      const address = `${toAddress}${toZip}${toCity}`
        .toLowerCase()
        .replaceAll(' ', '');
      return (
        companiesDict[address]?.name ??
        `${toAddress}, ${toZip} ${toCity}`
      );
    },
    [companiesDict]
  );

  // Helper function to determine if a column should show content for summary rows
  const shouldShowInSummary = (columnHead: string, model: any) => {
    if (!(model as any)?.isSummaryRow) return true; // Always show for non-summary rows

    const summaryColumns = [
      'Estimerad tid',
      'Arbetad tid',
      'Utlägg',
      'Rast',
      'CircleK',
      'Fakturera extra',
    ];
    // These columns should be explicitly hidden in summary rows. Miltal, Från,
    // Till, Starttid and Sluttid are per-assignment values that can't be
    // meaningfully summed, so we blank them out on the summary row.
    const hiddenSummaryColumns = [
      'Bokad tid',
      'Est. start',
      'Status',
      'Godkännbarhets-check',
      'Ärendetyp',
      'Miltal',
      'Från',
      'Till',
      'Starttid',
      'Sluttid',
    ];
    // If it's explicitly in the hidden list, don't show it
    if (hiddenSummaryColumns.includes(columnHead)) {
      return false;
    }
    // Otherwise, only show if it's in the allowed summary columns
    return summaryColumns.includes(columnHead);
  };

  // Helper function to wrap render functions to conditionally show content for summary rows
  const wrapRenderForSummary = useCallback(
    (columnHead: string, renderFn: any) => {
      return (model: any, ...args: any[]) => {
        if (!shouldShowInSummary(columnHead, model)) {
          return null;
        }
        return renderFn(model, ...args);
      };
    },
    []
  );

  const columnSettings = useMemo<
    ColumnSetting<AssignmentWithAutoApprovalCondition, ColumnRenderProps>[]
  >(() => {
    const result: (
      | ColumnSetting<AssignmentWithAutoApprovalCondition, ColumnRenderProps>
      | false
    )[] = [
      {
        head: 'Välj',
        render: wrapRenderForSummary(
          'Välj',
          (
            model: AssignmentWithAutoApprovalCondition,
            { onSelectClick, selectedAssignmentIds }: ColumnRenderProps
          ) => {
            // Checkbox endast på rader i godkännbar status (Slutfört/Avbokat) —
            // speglar backend-vakten. Inom den mängden visas checkboxen oavsett
            // autogodkännbarhetsstatus, så att admin kan bulk-godkänna via
            // "Välj alla på sidan".
            if (
              !isApprovableStatus(model.assignment.assignment.assignmentStatusID)
            ) {
              return null;
            }

            // Manuellt godkännande är spärrat i backend till kl 02 dagen efter startdagen,
            // så att det nattliga autogodkännandet hinner utvärdera uppdraget först. Externa
            // lastbilstransportörer (isTruckTransport) är undantagna — visa då checkboxen
            // istället för låsikonen.
            const unlockAt = getManualApprovalUnlockTime(
              model.assignment.assignment.startTime,
              model.assignment.assignment.endTime
            );
            const manualApprovalLocked = !isManualApprovalUnlocked(
              model.assignment.assignment.startTime,
              model.assignment.assignment.endTime,
              model.assignment.assignment.assignedTo?.isTruckTransport
            );
            if (unlockAt && manualApprovalLocked) {
              return (
                <CheckboxCell
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <FontAwesomeIcon
                    icon={faLock}
                    title={`Kan godkännas manuellt tidigast ${formatDateTime(unlockAt)} — autogodkännandet ska hinna utvärdera uppdraget först`}
                  />
                </CheckboxCell>
              );
            }

            return (
              <CheckboxCell
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectClick(model.assignment as AssignmentViewModel);
                }}
              >
                <FakeCheckbox
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectClick(model.assignment as AssignmentViewModel);
                  }}
                  checked={selectedAssignmentIds.has(
                    model.assignment.assignment.assignmentID
                  )}
                />
              </CheckboxCell>
            );
          }
        ),
        css: css`
          max-width: 60px;

          &:not(.${tableHeadClassName}) {
            padding: 0;
          }
        `,
        width: 60,
      },
      {
        head: 'Nr',
        render: wrapRenderForSummary(
          'Nr',
          (_avm: any, _props: any, _focused: any, rowIndex: number) => (
            <span
              aria-label={String(rowIndex + 1)}
              title={String(rowIndex + 1)}
            >
              {rowIndex + 1}
            </span>
          )
        ),
        width: 50,
      },
      {
        head: 'Datum',
        render: wrapRenderForSummary(
          'Datum',
          (avm: AssignmentWithAutoApprovalCondition) =>
            formatDate(avm.assignment.assignment.bookedTo)
        ),
        width: 100,
        css: css`
          &.${tableCellClassName} {
            font-family: monospace;
          }
        `,
        sortFunction: (a, b) =>
          a.assignment.assignment.bookedTo > b.assignment.assignment.bookedTo
            ? 1
            : -1,
      },

      {
        head: 'Bokad tid',
        render: wrapRenderForSummary(
          'Bokad tid',
          (avm: AssignmentWithAutoApprovalCondition) =>
            formatTime(avm.assignment.assignment.bookedTo)
        ),
        width: 90,
        css: css`
          &.${tableCellClassName} {
            font-family: monospace;
          }
        `,
        sortFunction: (a, b) =>
          a.assignment.assignment.bookedTo > b.assignment.assignment.bookedTo
            ? 1
            : -1,
      },

      {
        head: 'Est. start',
        render: wrapRenderForSummary(
          'Est. start',
          (avm: AssignmentWithAutoApprovalCondition) =>
            avm.assignment.assignment.estimatedStartTime
              ? formatTime(avm.assignment.assignment.estimatedStartTime)
              : '–'
        ),
        width: 80,
        css: css`
          &.${tableCellClassName} {
            font-family: monospace;
          }
        `,
        sortFunction: (a, b) =>
          (a.assignment.assignment.estimatedStartTime ?? 0) >
          (b.assignment.assignment.estimatedStartTime ?? 0)
            ? 1
            : -1,
      },

      {
        identifier: TableColumnEnum.AssignmentUnreadComments,
        head: '',
        csvHead: 'Olästa kommentarer',
        css: css`
          &:not(.${tableHeadClassName}) {
            border-right: 0;
          }
          .notifyIcon {
            font-size: 12px;
            color: ${({ theme }) => theme.colors.foreground.newMessage};
          }
        `,
        csvValue: (avm) =>
          avm.assignment.case.adminHasUnreadComments
            ? 'Har olästa kommentarer'
            : 'Saknar olästa kommentarer',
        render: (avm) =>
          avm.assignment.case.adminHasUnreadComments && (
            <FontAwesomeIcon
              className="notifyIcon"
              fixedWidth
              icon={faComment}
              title="Det finns olästa kommentarer"
            />
          ),
        width: 22,
        sortFunction: makeBooleanComparator(
          (avm) => avm.assignment.case.adminHasUnreadComments
        ),
      },
      {
        head: '',
        css: css`
          &:not(.${tableHeadClassName}) {
            border-right: 0;
          }
          .notifyIcon {
            font-size: 12px;
            color: ${({ theme }) => theme.colors.foreground.error};
          }
        `,
        csvValue: (avm) => {
          const info: string[] = [];
          if (avm.assignment.assignment.hasUncheckedFlaggedForSpeeding) {
            info.push('Uppdraget har hastighetsöverträdelser');
          }
          if (avm.assignment.assignment.assigneeReportedMissingGear) {
            info.push('Fälttestaren saknar arbetsutrustning vid start');
          }
          return info.join(', ');
        },
        render: (avm) => {
          const icons = [];
          if (avm.assignment.assignment.hasUncheckedFlaggedForSpeeding) {
            icons.push(
              <FontAwesomeIcon
                className="notifyIcon"
                fixedWidth
                icon={faTaxi}
                title="Uppdraget har hastighetsöverträdelser"
              />
            );
          }
          if (avm.assignment.assignment.assigneeReportedMissingGear) {
            icons.push(
              <FontAwesomeIcon
                className="notifyIcon"
                fixedWidth
                icon={faToolbox}
                title="Fälttestaren saknar arbetsutrustning vid start"
              />
            );
          }
          return (
            <>
              {icons.map((i) => (
                <span key={i.key || i.props.title}>{i}</span>
              ))}
            </>
          );
        },
        width: 22,
        sortFunction: makeBooleanComparator((avm) => {
          const avmHasAnIssue =
            avm.assignment.assignment.hasUncheckedFlaggedForSpeeding ||
            (avm.assignment.assignment.assigneeHasGreenPlate === false &&
              avm.assignment.assignment.assignedTo?.userID !== undefined &&
              (users[avm.assignment.assignment.assignedTo.userID]?.greenPlate
                ?.length ?? 0) > 0) ||
            avm.assignment.assignment.assigneeHasOBDMeter === false ||
            avm.assignment.assignment.assigneeHasTyreMeter === false ||
            avm.assignment.assignment.assigneeHasWorkClothes === false;
          return avmHasAnIssue;
        }),
      },
      {
        identifier: TableColumnEnum.AssignmentWarnings,
        head: '',
        csvHead: 'Varningar',
        css: css`
          &:not(.${tableHeadClassName}) {
            border-right: 0;
          }
          .notifyIcon {
            font-size: 12px;
            color: ${({ theme }) => theme.colors.foreground.error};
          }
        `,
        csvValue: (avm) => {
          const warnings: WarningInfo[] =
            assignmentWarnings.getCaseAndAssignmentWarnings(avm.assignment);
          return warnings.map((w) => w.warning).join(', ');
        },
        render: (avm) => {
          const warnings = assignmentWarnings.getCaseAndAssignmentWarnings(
            avm.assignment
          );
          return <ExpandableWarningsWidget warnings={warnings} />;
        },
        width: 22,
        sortFunction: (a, b) => {
          const aIssues = assignmentWarnings.getCaseAndAssignmentWarnings(
            a.assignment
          ).length;
          const bIssues = assignmentWarnings.getCaseAndAssignmentWarnings(
            b.assignment
          ).length;

          return aIssues - bIssues;
        },
      },

      {
        head: 'Ärendetyp',
        render: wrapRenderForSummary(
          'Ärendetyp',
          (
            avm: AssignmentWithAutoApprovalCondition,
            { translations: { caseTypes } }: ColumnRenderProps
          ) => caseTypes[avm.assignment.case.caseTypeID]
        ),
        width: 100,
        sortFunction: (a, b) =>
          specialBulkSort(
            a.assignment as AssignmentViewModel,
            b.assignment as AssignmentViewModel
          ),
      },
      {
        head: 'Uppdragstyp',
        render: (avm, { translations: { assignmentTypes } }) =>
          assignmentTypes[avm.assignment.assignment.assignmentTypeID],
        width: 100,
        sortFunction: (a, b) =>
          specialBulkSort(
            a.assignment as AssignmentViewModel,
            b.assignment as AssignmentViewModel
          ),
      },
      {
        head: 'Status',
        render: wrapRenderForSummary(
          'Status',
          (
            avm: AssignmentWithAutoApprovalCondition,
            { translations: { assignmentStatuses } }: ColumnRenderProps
          ) => {
            const statusText =
              assignmentStatuses[avm.assignment.assignment.assignmentStatusID];
            const isAutoApproved =
              avm.assignment.assignment.assignmentStatusID ===
                AssignmentStatusEnum.Approved &&
              !!avm.assignment.assignment.autoApprovedAt;
            if (isAutoApproved) {
              return (
                <StatusWithIcon>
                  <span>{statusText}</span>
                  <AiSparkle title="Auto-godkänt" />
                </StatusWithIcon>
              );
            }
            return statusText;
          }
        ),
        width: 80,
        sortFunction: (a, b) =>
          specialBulkSort(
            a.assignment as AssignmentViewModel,
            b.assignment as AssignmentViewModel
          ),
      },
      {
        head: 'Regnr',
        csvValue: (avm) => avm.assignment.case.registrationNumber,
        render: (avm: AssignmentWithAutoApprovalCondition) =>
          `${avm.assignment.case.registrationNumber}`,
        width: 70,
        sortFunction: (a, b) =>
          a.assignment.case.registrationNumber >
          b.assignment.case.registrationNumber
            ? 1
            : -1,

        css: css`
          &.${tableCellClassName} {
            font-family: monospace;
          }
        `,
      },

      {
        head: 'Miltal',
        csvValue: (avm) => String(avm.assignment.case.vehicleMileage),
        render: wrapRenderForSummary(
          'Miltal',
          (avm: AssignmentWithAutoApprovalCondition) =>
            avm.assignment.case.vehicleMileage
        ),
        width: 80,
        sortFunction: makeNumberComparator(
          (avm) => avm.assignment.case.vehicleMileage
        ),
      },
      {
        identifier: TableColumnEnum.AssignmentFromAddress,
        head: 'Från',
        csvValue: (avm) => {
          const { fromName, fromAddress, fromZip, fromCity } =
            avm.assignment.assignment;
          const addressString = `${fromAddress} ${fromZip} ${fromCity}`.replaceAll(
            ',',
            ''
          );
          const isLogistics = fromName.startsWith('Logistik');

          if (isLogistics) {
            return logisticsStrangnas?.name ?? addressString;
          }
          let addr = `${fromAddress}${fromZip}${fromCity}`;
          addr = addr.toLowerCase().replaceAll(' ', '');

          return companiesDict[addr]?.name ?? addressString;
        },
        render: wrapRenderForSummary(
          'Från',
          (avm: AssignmentWithAutoApprovalCondition) => {
            const { fromName, fromAddress, fromZip, fromCity } =
              avm.assignment.assignment;
            const addressString = `${fromAddress} ${fromZip} ${fromCity}`;
            const isLogistics = fromName.startsWith('Logistik');

            if (isLogistics) {
              return logisticsStrangnas?.name ?? addressString;
            }
            let addr = `${fromAddress}${fromZip}${fromCity}`;
            addr = addr.toLowerCase().replaceAll(' ', '');
            const dictHit = companiesDict[addr];
            if (dictHit) {
              return `${dictHit.name} – ${addressString}`;
            }
            return addressString;
          }
        ),
        width: 320,
        sortFunction: (a, b) =>
          getVisibleFromAddressName(a).localeCompare(
            getVisibleFromAddressName(b)
          ),
      },
      {
        identifier: TableColumnEnum.AssignmentToAddress,
        head: 'Till',
        csvValue: (avm) => {
          const { toName, toAddress, toZip, toCity } =
            avm.assignment.assignment;
          const addressString = `${toAddress} ${toZip} ${toCity}`;
          const isLogistics = toName.startsWith('Logistik');

          if (isLogistics) {
            return logisticsStrangnas?.name ?? addressString;
          }
          let addr = `${toAddress}${toZip}${toCity}`;
          addr = addr.toLowerCase().replaceAll(' ', '');

          return companiesDict[addr]?.name ?? addressString;
        },
        render: wrapRenderForSummary(
          'Till',
          (avm: AssignmentWithAutoApprovalCondition) => {
            const { toName, toAddress, toZip, toCity } =
              avm.assignment.assignment;
            const addressString = `${toAddress} ${toZip} ${toCity}`;
            const isLogistics = toName.startsWith('Logistik');

            if (isLogistics) {
              return logisticsStrangnas?.name ?? addressString;
            }
            let addr = `${toAddress}${toZip}${toCity}`;
            addr = addr.toLowerCase().replaceAll(' ', '');

            return companiesDict[addr]?.name ?? addressString;
          }
        ),
        width: 320,
        sortFunction: (a, b) =>
          getVisibleToAddressName(a).localeCompare(getVisibleToAddressName(b)),
      },

      {
        head: 'Fälttestare',
        render: (assignmentViewModel) =>
          assignmentViewModel.assignment.assignment.assignedTo?.name,
        width: 150,
        sortFunction: (a, b) =>
          (a.assignment.assignment.assignedTo?.name ?? '') >
          (b.assignment.assignment.assignedTo?.name ?? '')
            ? 1
            : -1,
      },
      {
        head: 'Starttid',
        render: wrapRenderForSummary(
          'Starttid',
          (avm: AssignmentWithAutoApprovalCondition) =>
            formatTime(avm.assignment.assignment.startTime)
        ),
        width: 80,
        css: css`
          &.${tableCellClassName} {
            font-family: monospace;
          }
        `,
        sortFunction: (a, b) =>
          (a.assignment.assignment.startTime ?? 0) >
          (b.assignment.assignment.startTime ?? 0)
            ? 1
            : -1,
      },
      {
        head: 'Sluttid',
        render: wrapRenderForSummary(
          'Sluttid',
          (avm: AssignmentWithAutoApprovalCondition) =>
            formatTime(avm.assignment.assignment.endTime)
        ),
        width: 80,
        css: css`
          &.${tableCellClassName} {
            font-family: monospace;
          }
        `,
        sortFunction: (a, b) =>
          (a.assignment.assignment.endTime ?? 0) >
          (b.assignment.assignment.endTime ?? 0)
            ? 1
            : -1,
      },
      {
        head: 'Rast',
        render: (avm) => `${avm.assignment.assignment.breakTime} min`,
        width: 100,
        sortFunction: (a, b) =>
          a.assignment.assignment.breakTime - b.assignment.assignment.breakTime,
      },
      {
        head: 'Arbetad tid',
        render: (avm) => avm.assignment.assignment.workedTimeDisplay,
        width: 100,
        sortFunction: makeNumberComparator(
          (avm) =>
            (avm.assignment.assignment.endTime?.getTime() ?? 0) -
            (avm.assignment.assignment.startTime?.getTime() ?? 0)
        ),
      },
      {
        head: 'Estimerad tid',
        render: (avm) =>
          getHoursAndMinutesFromMillisecondsString(
            avm.assignment.assignment.estimatedDuration ?? 0
          ),
        width: 100,
        sortFunction: makeNumberComparator(
          (avm) => avm.assignment.assignment.estimatedDuration ?? 0
        ),
      },
      {
        head: 'Utlägg',
        render: (avm) => {
          return (
            <>
              {`${avm.assignment.assignment.expenseSum} kr`}{' '}
              {avm.assignment.assignment.expectedExpenses.some(
                (ee) =>
                  !avm.expenses.some(
                    (e) =>
                      e.expenseType === ee.type &&
                      e.assignmentID === ee.assignmentId &&
                      e.approvedBy !== null
                  )
              ) ? (
                <CustomIcon icon={faExclamationCircle} />
              ) : null}
            </>
          );
        },
        width: 100,
        sortFunction: (a, b) =>
          a.assignment.assignment.expenseSum -
          b.assignment.assignment.expenseSum,
      },
      {
        head: 'CircleK',
        render: (avm) => {
          return <>{`${avm.assignment.assignment.circleKExpenseSum} kr`} </>;
        },
        width: 100,
        sortFunction: (a, b) =>
          a.assignment.assignment.expenseSum -
          b.assignment.assignment.expenseSum,
      },
      {
        head: 'Standby-tid',
        csvValue: (avm) => {
          const standby = avm.assignment.assignment.standByAssignmentTime;
          return standby == null
            ? ''
            : getHoursAndMinutesFromMillisecondsString(standby);
        },
        render: wrapRenderForSummary(
          'Standby-tid',
          (avm: AssignmentWithAutoApprovalCondition) => {
            const standby = avm.assignment.assignment.standByAssignmentTime;
            return standby == null
              ? '–'
              : getHoursAndMinutesFromMillisecondsString(standby);
          }
        ),
        width: 100,
        // Osatt sorteras före 0 så att uppdrag som saknar standbytid hamnar samlat.
        sortFunction: makeNumberComparator(
          (avm) => avm.assignment.assignment.standByAssignmentTime ?? -1
        ),
      },
      hasAccessToInvoiceExtras && {
        // In Efterkontroll the invoice extras can't be acted on (you have to
        // open the case), so we only show the summed amount of confirmed rows.
        head: 'Fakturera extra',
        render: wrapRenderForSummary(
          'Fakturera extra',
          (avm: AssignmentWithAutoApprovalCondition) => {
            const confirmedSum = (avm.assignment.assignment.invoiceExtras || [])
              .filter((r) => r.status === InvoiceExtraStatus.Confirmed)
              .reduce((sum, r) => sum + (r.value ?? 0), 0);
            return confirmedSum !== 0 ? formatKr(confirmedSum) : '–';
          }
        ),
        csvValue: (avm) => {
          const rows = (avm.assignment.assignment.invoiceExtras || [])
            .filter((r) => r.status !== InvoiceExtraStatus.Removed);
          return rows
            .map(
              (r) =>
                `[${r.status === InvoiceExtraStatus.Confirmed ? 'Bekräftad' : 'Förslag'}] ${r.type}: ${r.value} kr – ${(r.reason || '').replaceAll('\n', ' ')}`
            )
            .join('\n');
        },
        width: 120,
        sortFunction: makeNumberComparator((avm) => {
          const rows = avm.assignment.assignment.invoiceExtras || [];
          return rows
            .filter((r) => r.status === InvoiceExtraStatus.Confirmed)
            .reduce((sum, r) => sum + (r.value ?? 0), 0);
        }),
      },
      {
        head: 'Godkännbarhets-check',
        render: wrapRenderForSummary(
          'Godkännbarhets-check',
          (avm: AssignmentWithAutoApprovalCondition) => (
            <ApprovalChecksWidget
              result={avm.result}
              assignmentID={avm.assignmentID}
              checkResultId={avm.checkResultId}
              assignmentStatusID={avm.assignment.assignment.assignmentStatusID}
              autoApprovedAt={avm.assignment.assignment.autoApprovedAt}
              gracePeriodPassed={avm.gracePeriodPassed}
              autoApproveEligibleAt={getManualApprovalUnlockTime(
                avm.assignment.assignment.startTime,
                avm.assignment.assignment.endTime
              )}
              autoApprove={avm.assignment.assignment.autoApprove}
            />
          )
        ),
        width: 300,
        sortFunction: (
          a: AssignmentWithAutoApprovalCondition,
          b: AssignmentWithAutoApprovalCondition
        ) => specialBulkSort(a.assignment, b.assignment),
        css: css`
          white-space: pre-wrap;
          line-height: 1.2;
        `,
      },
    ];

    return result.filter(
      (
        c
      ): c is ColumnSetting<
        AssignmentWithAutoApprovalCondition,
        ColumnRenderProps
      > => !!c
    );
  }, [
    assignmentWarnings,
    users,
    wrapRenderForSummary,
    hasAccessToInvoiceExtras,
    companiesDict,
    logisticsStrangnas,
    getVisibleFromAddressName,
    getVisibleToAddressName,
  ]);

  return columnSettings;
};

export default useAutoApprovalTableColumnSettings;
